// Koppling mellan skyddsrondens avvikelser och arbetsberedningar.
// En avvikelse kan kräva en arbetsberedning; när beredningen godkänns
// markeras rondpunkten som åtgärdad.
import { supabase } from "@/integrations/supabase/client";
import { ensurePhaseFolder } from "@/lib/project/projectFolders";

export interface SafetyRoundSource {
  itemId: string;
  controlPoint: string;
  roundName: string;
  roundDate: string | null;
}

/** Skapar en arbetsberedning som svarar mot en avvikelse i en skyddsrond. */
export async function createWorkPrepForRoundItem(params: {
  projectId: string;
  userId: string;
  itemId: string;
  controlPoint: string;
  roundName: string;
  comment?: string | null;
  folderId?: string | null;
}): Promise<{ workPrepId: string; folderId: string } | null> {
  const folderId =
    params.folderId ??
    (await ensurePhaseFolder(params.projectId, params.userId, "30", "arbetsberedning"));
  if (!folderId) return null;

  const name = `Arbetsberedning – ${params.controlPoint}`.slice(0, 120);

  const { data, error } = await supabase
    .from("work_preparations")
    .insert({
      project_id: params.projectId,
      folder_id: folderId,
      name,
      status: "utkast",
      review_status: "utkast",
      source_safety_round_item_id: params.itemId,
      created_by: params.userId,
      data: {
        fields: {
          arbetsmoment: params.controlPoint,
          beskrivning: params.comment ?? "",
        },
        mode: "standard",
        origin: {
          type: "safety_round",
          roundName: params.roundName,
          controlPoint: params.controlPoint,
        },
      } as never,
    })
    .select("id")
    .single();

  if (error || !data) return null;

  await supabase
    .from("safety_round_items")
    .update({ work_preparation_id: data.id as string })
    .eq("id", params.itemId);

  return { workPrepId: data.id as string, folderId };
}

/**
 * Skriver tillbaka åtgärdsstatus på rondpunkten när beredningens
 * granskningsstatus ändras.
 */
export async function syncRoundItemFromWorkPrep(
  workPrepId: string,
  status: string,
  userId: string | null,
): Promise<void> {
  const approved = status === "godkand";
  await supabase
    .from("safety_round_items")
    .update({
      resolved_at: approved ? new Date().toISOString() : null,
      resolved_by: approved ? userId : null,
    })
    .eq("work_preparation_id", workPrepId);
}

/** Hämtar rondpunkten som en arbetsberedning svarar mot. */
export async function loadSafetyRoundSource(
  workPrepId: string,
): Promise<SafetyRoundSource | null> {
  const { data } = await supabase
    .from("safety_round_items")
    .select("id, control_point, safety_rounds(name, round_date)")
    .eq("work_preparation_id", workPrepId)
    .maybeSingle();

  if (!data) return null;
  const round = (data as never as { safety_rounds?: { name: string; round_date: string | null } })
    .safety_rounds;
  return {
    itemId: (data as { id: string }).id,
    controlPoint: (data as { control_point: string }).control_point,
    roundName: round?.name ?? "Skyddsrond",
    roundDate: round?.round_date ?? null,
  };
}
